/**
 * JsonStructureDetailsDialog — sub-dialog opened from the MetaInfo JSON
 * columns list ("Details" button), and from grid cell / column-header
 * context menus when the user picks "JSON details" on an already-declared
 * column. Shows the inferred JSON shape from the structure cache so the user
 * can review what was discovered, refresh the sample on demand (optionally
 * with a filter), and undeclare the column if needed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link2, ListPlus, Trash2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DocsLinkButton } from '@/features/help-feedback';
import { JsonStructureInspector } from './JsonStructureInspector';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import type { JsonShapeEnumCtx, JsonShapeLooseFkCtx } from '../JsonShapePreview';
import type { JsonStructureCache } from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { isNativeJsonColumn } from '../../utils/jsonColumnCategorization';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { isEscapeClaimTarget } from '../../utils/escapeClaimTarget';

export type JsonStructureDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  table: string;
  column: string;
  cache: JsonStructureCache | null;
  /** Refetch context — when present, the inspector renders a Refresh +
   * Filter affordance that re-samples the column from the database. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /** Engine-typed column type. When provided AND the type is a native JSON
   * type, a small "Native JSON column" note is shown under the title. */
  dataType?: string;
  /** When provided, a "Remove declaration" footer button is rendered. The
   * parent handles the confirmation flow and persistence. */
  onRemoveDeclaration?: () => void;
};

export function JsonStructureDetailsDialog({
  open,
  onOpenChange,
  schema,
  table,
  column,
  cache,
  connectionId,
  database,
  engine,
  dataType,
  onRemoveDeclaration,
}: JsonStructureDetailsDialogProps) {
  const isNative =
    !!engine && !!dataType &&
    isNativeJsonColumn({ engine, schema, table, name: column, dataType });

  const { track } = useAnalytics();

  // Pick-mode lets the user click any property in the structure to declare it
  // either as an enum or as a loose-FK source. Two flavors are mutually
  // exclusive — only one can be active at a time. Captures the "name doesn't
  // suggest …" case the inline glyphs deliberately gate out (mirrors the
  // column-header right-click entries being broader than the inline glyphs).
  type PickModeKind = 'enum' | 'loose-fk' | null;
  const [pickMode, setPickMode] = useState<PickModeKind>(null);

  // Enum-state context for JsonShapePreview. Only enabled when the dialog
  // has a live connection (the read-only Settings → Meta Info mount has no
  // connectionId/database/engine — there's no IPC to declare against from
  // there anyway).
  const metaInfoQuery = useMetaInfo(connectionId ?? '', database ?? '');
  const enumColumnsForColumn = useMemo(() => {
    const list = metaInfoQuery.data?.enumColumns ?? [];
    return list.filter(
      (d) => d.schema === schema && d.table === table && d.column === column,
    );
  }, [metaInfoQuery.data, schema, table, column]);

  // Loose-FK relationships sourced from this column. Two consumers:
  //   1. The auto-exit `useEffect` watches the length to clear `loose-fk`
  //      pick-mode after the user confirms a new declaration via the editor.
  //   2. `JsonShapePreview` consumes the full list via `looseFkCtx` so the
  //      inline `PathLooseFkGlyph` can render next to declared paths.
  const looseRelsForColumn = useMemo(() => {
    const list = metaInfoQuery.data?.looseRelationships ?? [];
    return list.filter(
      (r) =>
        r.fromSchema === schema &&
        r.fromTable === table &&
        r.fromColumn === column,
    );
  }, [metaInfoQuery.data, schema, table, column]);
  const looseFkCountForColumn = looseRelsForColumn.length;

  const enumCtx: JsonShapeEnumCtx | undefined =
    connectionId && database && engine
      ? {
          connectionId,
          database,
          schema,
          table,
          column,
          engine,
          enumColumns: enumColumnsForColumn,
          pickMode: pickMode === 'enum',
          // Attribute ▾? clicks inside this dialog to its own surface — the
          // dialog can be reached from either the MetaInfo Manager or a grid
          // column-header right-click, both of which we want grouped under
          // "user was in the JSON details dialog".
          trigger: 'json-details-dialog',
        }
      : undefined;

  // Loose-FK pick context. Same connectivity guard as enumCtx — without a
  // live connection there's nothing to declare against.
  const looseFkCtx: JsonShapeLooseFkCtx | undefined =
    connectionId && database && engine
      ? {
          connectionId,
          database,
          schema,
          table,
          column,
          engine,
          dataType: dataType ?? '',
          pickMode: pickMode === 'loose-fk',
          looseRelationships: looseRelsForColumn,
        }
      : undefined;

  // Reset pick-mode when the dialog closes so re-opening starts clean.
  const handleOpenChange = (next: boolean) => {
    if (!next) setPickMode(null);
    onOpenChange(next);
  };

  // Fire the funnel-top signal when the user enters pick-mode. The confirm
  // side already fires `feature:enum_declare` from inside the declare dialog;
  // this captures users who enter pick-mode and abandon.
  const handleEnterEnumPickMode = () => {
    setPickMode('enum');
    if (engine) track('feature:enum_pickmode_enter', { engine });
  };

  // Parallel funnel-top for the loose-FK pick mode.
  const handleEnterLooseFkPickMode = () => {
    setPickMode('loose-fk');
    if (engine) track('feature:loose_fk_pickmode_enter', { engine });
  };

  // Auto-exit `loose-fk` pick-mode once a new relationship sourced from this
  // column appears in MetaInfo. The Loose-Relationship editor dialog is a
  // separate global dialog with no onSaved callback — we lean on the
  // reactive useMetaInfo query instead. Tracks the count snapshot at
  // pick-mode entry; if it increases while pickMode is 'loose-fk', clear it.
  const looseFkCountAtEntryRef = useRef<number | null>(null);
  useEffect(() => {
    if (pickMode === 'loose-fk') {
      if (looseFkCountAtEntryRef.current === null) {
        looseFkCountAtEntryRef.current = looseFkCountForColumn;
        return;
      }
      if (looseFkCountForColumn > looseFkCountAtEntryRef.current) {
        looseFkCountAtEntryRef.current = null;
        setPickMode(null);
      }
    } else {
      looseFkCountAtEntryRef.current = null;
    }
  }, [pickMode, looseFkCountForColumn]);

  // Parallel auto-exit for `enum` pick-mode. The DeclareEnumColumnDialog also
  // has no onSaved callback wired here — same reactive trick: snapshot the
  // enum-declaration count on entry, clear pick-mode when it grows. Filters
  // by column so an unrelated declaration elsewhere doesn't trip the exit.
  const enumDeclCountForColumn = enumColumnsForColumn.length;
  const enumDeclCountAtEntryRef = useRef<number | null>(null);
  useEffect(() => {
    if (pickMode === 'enum') {
      if (enumDeclCountAtEntryRef.current === null) {
        enumDeclCountAtEntryRef.current = enumDeclCountForColumn;
        return;
      }
      if (enumDeclCountForColumn > enumDeclCountAtEntryRef.current) {
        enumDeclCountAtEntryRef.current = null;
        setPickMode(null);
      }
    } else {
      enumDeclCountAtEntryRef.current = null;
    }
  }, [pickMode, enumDeclCountForColumn]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="metainfo-json-details-dialog"
        // F1-#2 — Escape-in-popover seam consolidation, extended to this
        // Dialog host. `JsonStructureInspector` mounts a `FilterWizard`
        // whose committed `FilterChip` claims Escape for its own in-place
        // value/JSON-draft/OR-draft edits via `escapeClaimProps` on the
        // chip's own root while editing; without this ancestor check
        // Radix's document-capture dismiss listener would win the race and
        // close the whole dialog instead of just cancelling the edit (see
        // `escapeClaimTarget.ts`'s docblock for why a per-input
        // `preventDefault()` can never win that race on its own).
        onEscapeKeyDown={(e) => {
          if (isEscapeClaimTarget(e.target)) e.preventDefault();
        }}
      >
        <button
          type="button"
          onClick={() => handleOpenChange(false)}
          className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label="Close"
          data-testid="json-details-close-x"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>JSON structure</DialogTitle>
            <DocsLinkButton
              url="https://jamsql.com/docs/json-columns/"
              doc="json-columns"
            />
          </div>
          <DialogDescription>
            <span translate="no" className="font-mono text-xs">
              {schema}.{table}.{column}
            </span>
          </DialogDescription>
          {isNative && (
            <p
              className="mt-1 text-[11px] text-muted-foreground"
              data-testid="json-details-native-note"
            >
              Native JSON column — declaration stores path metadata and
              loose-FK targets only.
            </p>
          )}
        </DialogHeader>
        {pickMode && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-300"
            data-testid="json-details-pickmode-banner"
            data-pickmode={pickMode}
            role="status"
          >
            {pickMode === 'enum'
              ? 'Click any property below to declare it as an enum.'
              : 'Click any property below to declare a loose FK on it.'}{' '}
            <button
              type="button"
              className="underline underline-offset-2 hover:no-underline"
              onClick={() => setPickMode(null)}
              data-testid="json-details-pickmode-cancel"
            >
              Cancel
            </button>
          </div>
        )}
        <JsonStructureInspector
          schema={schema}
          table={table}
          column={column}
          initialCache={cache}
          connectionId={connectionId}
          database={database}
          engine={engine}
          source="details-dialog"
          enumCtx={enumCtx}
          looseFkCtx={looseFkCtx}
        />
        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
          <div className="flex items-center gap-2">
            {(enumCtx || looseFkCtx) && !pickMode && (
              <>
                <span
                  className="text-xs text-muted-foreground"
                  data-testid="json-details-declare-property-as-label"
                >
                  Declare property as:
                </span>
                {enumCtx && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEnterEnumPickMode}
                    data-testid="json-details-declare-property-as-enum"
                    className="h-7 px-2 text-xs"
                  >
                    <ListPlus className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    enum
                  </Button>
                )}
                {looseFkCtx && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEnterLooseFkPickMode}
                    data-testid="json-details-declare-property-as-loose-fk"
                    className="h-7 px-2 text-xs"
                  >
                    <Link2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    loose FK
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onRemoveDeclaration && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRemoveDeclaration}
                className="text-destructive hover:text-destructive"
                data-testid="json-details-remove-declaration"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Remove JSON declaration
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => handleOpenChange(false)}
              data-testid="json-details-close"
            >
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
